import { useState, useEffect } from 'react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

type Platform = 'android-chrome' | 'ios-safari' | 'desktop' | 'already-installed';

function detectPlatform(): Platform {
  if (window.matchMedia('(display-mode: standalone)').matches) return 'already-installed';
  const ua = navigator.userAgent;
  const isIOS = /iPad|iPhone|iPod/.test(ua) && !(window as any).MSStream;
  const isSafari = /^((?!chrome|android).)*safari/i.test(ua);
  if (isIOS && isSafari) return 'ios-safari';
  const isAndroid = /Android/.test(ua);
  const isChrome = /Chrome/.test(ua);
  if (isAndroid && isChrome) return 'android-chrome';
  return 'desktop';
}

interface Props {
  onClose: () => void;
}

export function InstallGuideModal({ onClose }: Props) {
  const [platform] = useState<Platform>(detectPlatform);
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [step, setStep] = useState(0);
  const [installing, setInstalling] = useState(false);

  useEffect(() => {
    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleAndroidInstall = async () => {
    if (deferredPrompt) {
      setInstalling(true);
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      setInstalling(false);
      if (outcome === 'accepted') onClose();
    }
  };

  const iosSteps = [
    {
      icon: (
        <svg className="w-8 h-8 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
        </svg>
      ),
      title: 'Tryk på Del-knappen',
      desc: 'Find firkanten med pil op i bunden af Safari-browseren.',
    },
    {
      icon: (
        <svg className="w-8 h-8 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
      ),
      title: 'Vælg "Føj til hjemmeskærm"',
      desc: 'Scroll ned i menuen og tryk på "Føj til hjemmeskærm" (Add to Home Screen).',
    },
    {
      icon: (
        <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      ),
      title: 'Tryk "Tilføj"',
      desc: 'Bekræft ved at trykke "Tilføj" øverst til højre. Appen vises nu på din hjemmeskærm.',
    },
  ];

  const pushNote = platform === 'ios-safari' ? (
    <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
      <strong>Push-notifikationer på iPhone:</strong> Kræver iOS 16.4+. Når appen er installeret,
      gå til <em>Notifikationsindstillinger</em> og aktiver push-notifikationer.
    </div>
  ) : null;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center" onClick={onClose}>
      <div className="absolute inset-0 bg-black/50" />
      <div
        className="relative bg-white w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl shadow-2xl p-6"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <img src="/icon-192x192.png" alt="Gribklan" className="w-10 h-10 rounded-xl" style={{ background: 'none' }} />
            <div>
              <h2 className="text-lg font-bold text-gray-900 leading-none">Føj til hjemmeskærm</h2>
              <p className="text-xs text-gray-500 mt-0.5">Gribklan Social som app</p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {platform === 'already-installed' && (
          <div className="text-center py-6">
            <svg className="mx-auto w-12 h-12 text-green-500 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="font-semibold text-gray-900">Appen er allerede installeret!</p>
            <p className="text-sm text-gray-500 mt-1">Du finder Gribklan på din hjemmeskærm.</p>
          </div>
        )}

        {platform === 'android-chrome' && (
          <>
            <p className="text-gray-600 text-sm mb-5">
              Installer Gribklan Social som en app på din telefon. Derefter kan du modtage push-notifikationer og bruge appen offline.
            </p>
            <div className="space-y-3 mb-5">
              {[
                'Hurtig adgang fra hjemmeskærmen',
                'Push-notifikationer om beskeder og begivenheder',
                'Fungerer delvist uden internet',
              ].map((b) => (
                <div key={b} className="flex items-center gap-2 text-sm text-gray-700">
                  <svg className="w-4 h-4 text-green-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  {b}
                </div>
              ))}
            </div>
            {deferredPrompt ? (
              <button
                onClick={handleAndroidInstall}
                disabled={installing}
                className="w-full py-3 bg-primary-green text-white font-semibold rounded-xl hover:bg-secondary-green transition-colors disabled:opacity-50"
              >
                {installing ? 'Installerer...' : 'Installer nu'}
              </button>
            ) : (
              <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800">
                Åbn siden i <strong>Chrome</strong> for at installere appen direkte.
              </div>
            )}
          </>
        )}

        {platform === 'ios-safari' && (
          <>
            <p className="text-gray-600 text-sm mb-5">
              Følg disse 3 trin i Safari for at installere Gribklan Social på din iPhone:
            </p>
            <div className="space-y-4 mb-5">
              {iosSteps.map((s, i) => (
                <div
                  key={i}
                  className={`flex items-start gap-4 p-3 rounded-xl transition-colors ${step === i ? 'bg-blue-50 border border-blue-200' : 'bg-gray-50'}`}
                  onClick={() => setStep(i)}
                >
                  <div className="flex-shrink-0 w-7 h-7 rounded-full bg-white border-2 border-current flex items-center justify-center text-xs font-bold text-blue-600 shadow-sm">
                    {i + 1}
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 text-sm">{s.title}</p>
                    <p className="text-gray-500 text-xs mt-0.5">{s.desc}</p>
                  </div>
                </div>
              ))}
            </div>
            {pushNote}
          </>
        )}

        {platform === 'desktop' && (
          <>
            <p className="text-gray-600 text-sm mb-4">
              På desktop kan du installere Gribklan Social via adresselinjen i Chrome/Edge — klik på installationsikonet (⊕) i højre side.
            </p>
            {pushNote}
          </>
        )}
      </div>
    </div>
  );
}
